/**
 * AdminChatSessionList — /admin/chat
 *
 * Lists all support chat sessions. Accessible to admin and superadmin only.
 * Sessions with escalated (unanswered) questions are highlighted.
 * Clicking a row navigates to /admin/chat/:sessionId for the full view + reply.
 */

import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Spinner } from "reactstrap";

const STATUS_META = {
  escalated: { label: "Needs Reply",  bg: "#fff3cd", color: "#856404", border: "#ffc107" },
  resolved:  { label: "Resolved",     bg: "#d4edda", color: "#155724", border: "#c3e6cb" },
  closed:    { label: "Closed",       bg: "#e2e3e5", color: "#383d41", border: "#d6d8db" },
  active:    { label: "Active",       bg: "#cce5ff", color: "#004085", border: "#b8daff" },
};

export default function AdminChatSessionList() {
  const { user: authUser } = useAuth();
  const history   = useHistory();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]  = useState(false);
  const [search, setSearch]       = useState("");
  const [filter, setFilter]       = useState("all"); // all | escalated | resolved | closed

  const isAdmin = authUser?.user_type === "admin" || authUser?.user_type === "superadmin";

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    axios.get("/chat/admin/sessions")
      .then(res => setSessions(res.data || []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#dc3545" }}>
        Access denied — admin only.
      </div>
    );
  }

  const filtered = sessions.filter(s => {
    const matchSearch = !search
      || s.username.toLowerCase().includes(search.toLowerCase())
      || s.last_message.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || s.status === filter;
    return matchSearch && matchFilter;
  });

  const escalatedCount = sessions.filter(s => s.status === "escalated").length;

  return (
    <div style={{
      padding: "24px 28px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      maxWidth: 900,
    }}>
      {/* ── header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#333" }}>
            Support Sessions
          </h2>
          {escalatedCount > 0 && (
            <span style={{
              background: "#dc3545", color: "#fff",
              borderRadius: 12, padding: "2px 10px",
              fontSize: 12, fontWeight: 700,
            }}>
              {escalatedCount} need reply
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: "#888" }}>
          All AI support conversations. Escalated sessions require your reply.
        </div>
      </div>

      {/* ── controls ── */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by user or message…"
          style={{
            flex: 1, minWidth: 200,
            border: "1px solid #e0e0e0", borderRadius: 8,
            padding: "8px 12px", fontSize: 13, outline: "none",
          }}
          onFocus={e => e.target.style.borderColor = "#667eea"}
          onBlur={e => e.target.style.borderColor = "#e0e0e0"}
        />
        <div style={{ display: "flex", gap: 6 }}>
          {["all", "escalated", "resolved", "closed"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: "7px 14px",
                borderRadius: 8,
                border: `1px solid ${filter === f ? "#667eea" : "#e0e0e0"}`,
                background: filter === f ? "#f0edff" : "#fff",
                color: filter === f ? "#667eea" : "#666",
                cursor: "pointer", fontSize: 12, fontWeight: filter === f ? 600 : 400,
                textTransform: "capitalize",
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* ── session table ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #eee", overflow: "hidden" }}>
        {/* table header */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "140px 1fr 120px 90px 90px",
          padding: "10px 20px",
          background: "#f8f9fa",
          borderBottom: "1px solid #eee",
          fontSize: 11, fontWeight: 700, color: "#888",
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}>
          <span>User</span>
          <span>Last Message</span>
          <span>Time</span>
          <span>Messages</span>
          <span>Status</span>
        </div>

        {loading && (
          <div style={{ padding: 40, textAlign: "center" }}>
            <Spinner size="sm" /> Loading…
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: "#aaa", fontSize: 14 }}>
            No sessions found.
          </div>
        )}

        {!loading && filtered.map((session, idx) => {
          const meta = STATUS_META[session.status] || STATUS_META.closed;
          return (
            <div
              key={session.id}
              onClick={() => history.push(`/admin/chat/${session.id}`)}
              style={{
                display: "grid",
                gridTemplateColumns: "140px 1fr 120px 90px 90px",
                padding: "14px 20px",
                borderBottom: idx < filtered.length - 1 ? "1px solid #f0f0f0" : "none",
                cursor: "pointer",
                background: session.status === "escalated" ? "#fffbf0" : "#fff",
                transition: "background 0.15s",
                alignItems: "center",
              }}
              onMouseEnter={e => e.currentTarget.style.background = session.status === "escalated" ? "#fff8e0" : "#f8f8f8"}
              onMouseLeave={e => e.currentTarget.style.background = session.status === "escalated" ? "#fffbf0" : "#fff"}
            >
              <div style={{ fontWeight: 600, fontSize: 13, color: "#333" }}>
                {session.username}
              </div>
              <div style={{
                fontSize: 13, color: "#555",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                paddingRight: 16,
              }}>
                {session.status === "escalated" && "⚠️ "}
                {session.last_message}
              </div>
              <div style={{ fontSize: 12, color: "#aaa" }}>
                {formatRelativeTime(session.updated_at || session.created_at)}
              </div>
              <div style={{ fontSize: 13, color: "#888", textAlign: "center" }}>
                {session.message_count}
              </div>
              <div>
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  background: meta.bg, color: meta.color,
                  border: `1px solid ${meta.border}`,
                  borderRadius: 12, padding: "3px 9px",
                  whiteSpace: "nowrap",
                }}>
                  {meta.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 12, color: "#aaa", marginTop: 12, textAlign: "right" }}>
        {filtered.length} session{filtered.length !== 1 ? "s" : ""}
        {filter !== "all" ? ` (${filter})` : ""} · All times are IST
      </div>
    </div>
  );
}

function formatRelativeTime(ts) {
  if (!ts) return "";
  const diff = Date.now() - (ts instanceof Date ? ts.getTime() : new Date(ts).getTime());
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}
